import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    addLikedItem as addLikedItemApi,
    getLikedItemIds,
    removeLikedItem as removeLikedItemApi
} from "@/api/customer.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { ROLES } from "@/utils/constants.js";

const FavoritesContext = createContext(null);

function applyIdsPayload(data) {
    const ids = (data?.productIds || []).map(String);
    return {
        ids: new Set(ids),
        count: typeof data?.count === "number" ? data.count : ids.length
    };
}

export function FavoritesProvider({ children }) {
    const { isAuthenticated, role } = useAuth();
    const [productIds, setProductIds] = useState(() => new Set());
    const [itemCount, setItemCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState("");

    const refreshFavorites = useCallback(async () => {
        if (!isAuthenticated || role !== ROLES.CUSTOMER) {
            setProductIds(new Set());
            setItemCount(0);
            return;
        }
        setLoading(true);
        try {
            const data = await getLikedItemIds();
            const next = applyIdsPayload(data);
            setProductIds(next.ids);
            setItemCount(next.count);
        } catch {
            setProductIds(new Set());
            setItemCount(0);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, role]);

    useEffect(() => {
        refreshFavorites();
    }, [refreshFavorites]);

    useEffect(() => {
        if (!feedback) {
            return undefined;
        }
        const timer = window.setTimeout(() => setFeedback(""), 2200);
        return () => window.clearTimeout(timer);
    }, [feedback]);

    const isFavorite = useCallback(
        (productId) => productIds.has(String(productId)),
        [productIds]
    );

    const toggleFavorite = useCallback(async (productId) => {
        const id = String(productId);
        const wasFavorite = productIds.has(id);
        const data = wasFavorite
            ? await removeLikedItemApi(productId)
            : await addLikedItemApi(productId);
        const next = applyIdsPayload(data);
        setProductIds(next.ids);
        setItemCount(next.count);
        setFeedback(wasFavorite ? "Retiré de « J'aime »" : "Ajouté à « J'aime »");
        return { added: !wasFavorite, removed: wasFavorite };
    }, [productIds]);

    const addFavorite = useCallback(async (productId) => {
        if (productIds.has(String(productId))) {
            setFeedback("Déjà dans « J'aime »");
            return { added: false, removed: false };
        }
        const data = await addLikedItemApi(productId);
        const next = applyIdsPayload(data);
        setProductIds(next.ids);
        setItemCount(next.count);
        setFeedback("Ajouté à « J'aime »");
        return { added: true, removed: false };
    }, [productIds]);

    const removeFavorite = useCallback(async (productId) => {
        const data = await removeLikedItemApi(productId);
        const next = applyIdsPayload(data);
        setProductIds(next.ids);
        setItemCount(next.count);
        setFeedback("Retiré de « J'aime »");
        return { added: false, removed: true };
    }, []);

    const value = useMemo(
        () => ({
            productIds,
            itemCount,
            loading,
            feedback,
            clearFeedback: () => setFeedback(""),
            refreshFavorites,
            isFavorite,
            toggleFavorite,
            addFavorite,
            removeFavorite
        }),
        [
            productIds,
            itemCount,
            loading,
            feedback,
            refreshFavorites,
            isFavorite,
            toggleFavorite,
            addFavorite,
            removeFavorite
        ]
    );

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error("useFavorites must be used within FavoritesProvider");
    }
    return context;
}
